<script setup lang="ts">
import { MMLink, MMPopupDefault, MMText } from '@mm-frontend/mm-web-ui-kit';
import { useLegalSDK } from '../business/useLegalSDK';
import { ButtonPrimaryAccented, ButtonSecondaryNeutral, CheckboxBase, DropdownInput } from '~/features/ui-kit';
import { DadataClient } from '~/shared/api/ke-gateway/dadata/DadataClient';
import { useDebounce } from '@vueuse/core';
import type { ISuggestion, IPartyDTO } from '~/shared/api/ke-gateway/dadata/models';

const { addCompany } = useLegalSDK();

const emit = defineEmits<{ (e: 'close'): void }>();

const isLoading = ref(false);
const companyName = ref<{ value: string } | null>(null);
const company = ref<IPartyDTO | null>(null);
const queryStr = ref<string>('');
const debouncedQueryStr = useDebounce(queryStr, 300);

function onSelectCompany(value: ISuggestion<IPartyDTO>, hideFn: () => void) {
  company.value = value.data;
  companyName.value = value;
  hideFn();
}

const suggestions = ref<ISuggestion<IPartyDTO>[]>([]);
async function onUpdateQueryStr(value: string) {
  isLoading.value = true;
  queryStr.value = value;
}

watch(debouncedQueryStr, async () => {
  suggestions.value = await DadataClient.getPartySuggests({ query: debouncedQueryStr.value }) || [];
  isLoading.value = false;
});

const isChecked = ref(false);

const isSubmitPending = ref(false);
async function onSubmit() {
  try {
    isSubmitPending.value = true;
    await addCompany({
      inn: company.value?.inn ?? '',
      kpp: company.value?.kpp ?? '',
      address: company.value?.address.value ?? '',
    });

    emit('close');
  } catch (error) {
    console.error(error);
  } finally {
    isSubmitPending.value = false;
  }
}
</script>

<template>
  <MMPopupDefault class="add-legal-account-popup" @close="$emit('close')">
    <template #header>
      <MMText font="Title M/Semibold">Добавьте компанию</MMText>
    </template>

    <template #default>
      <div class="add-legal-account">
        <DropdownInput
          :query-str="queryStr"
          :options="suggestions"
          :model-value="companyName"
          :is-loading="isLoading"
          placeholder="Введите ИНН организации"
          container=".add-legal-account"
          @update:query-str="(value) => onUpdateQueryStr(value)"
          @update:model-value="(value, hideFn) => onSelectCompany(value as ISuggestion<IPartyDTO>, hideFn)"
        >
          <template #option="{ option }">
            <div class="add-legal-account__option">
              <MMText font="Body M/Medium">{{ option.value }}</MMText>
              <MMText font="Caption S/Medium">{{ (option as ISuggestion<IPartyDTO>).data?.inn ?? '' }}</MMText>
            </div>
          </template>
        </DropdownInput>

        <div v-if="!isLoading && suggestions?.length === 0 && queryStr">
          <MMText font="Body M/Regular" color="negative">Не нашли компанию с таким ИНН</MMText>
        </div>

        <div v-if="!company" class="add-legal-account__info">
          <div class="add-legal-account__info-item">
            <MMText font="Body M/Medium">1</MMText>
            <MMText font="Body M/Bold">Оплата покупок</MMText>
            <MMText font="Body M/Regular">Оплачивайте покупки с внутреннего счета</MMText>
          </div>
          <div class="add-legal-account__info-item">
            <MMText font="Body M/Medium">2</MMText>
            <MMText font="Body M/Bold">Документооборот</MMText>
            <MMText font="Body M/Regular">Получайте сопроводительные документы</MMText>
          </div>
          <div class="add-legal-account__info-item">
            <MMText font="Body M/Medium">3</MMText>
            <MMText font="Body M/Bold">Налоговый вычет</MMText>
            <MMText font="Body M/Regular">Возмещайте НДС до 20%</MMText>
          </div>
        </div>

        <div v-else class="add-legal-account__presubmit">
          <MMText font="Body M/Semibold">{{ company.name.fullName }}</MMText>
          <MMText font="Body M/Medium">ИНН: {{ company.inn }}</MMText>
          <MMText font="Body M/Medium">КПП: {{ company.kpp }}</MMText>
          <MMText font="Body M/Medium">{{ company.address.value }}</MMText>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="add-legal-account__actions">
        <ButtonSecondaryNeutral v-if="!company" w100 @click="$emit('close')">Отмена</ButtonSecondaryNeutral>

        <template v-else >
          <CheckboxBase v-model="isChecked">
            <MMText font="Body M/Regular" class="add-legal-account__actions-checkbox">
              <span>Принимаю</span> 
              <MMLink to="https://legal.mm.ru/#dogovor_kp" target="_blank" underline="always">условия оферты</MMLink>
            </MMText>
          </CheckboxBase>
          <ButtonPrimaryAccented w100 :disabled="!isChecked" :loading="isSubmitPending" @click="onSubmit">Добавить компанию</ButtonPrimaryAccented>
        </template>
      </div>
    </template>
  </MMPopupDefault>
</template>

<style scoped lang="stylus">
.add-legal-account-popup
  width 420px
  min-height 460px

.add-legal-account
  display flex
  flex-direction column
  gap 18px
  padding 24px 0
  z-index 1

.add-legal-account__input
  margin-bottom 8px

.add-legal-account__option
  overflow hidden
  text-overflow ellipsis

.add-legal-account__info
  display flex
  flex-direction column
  gap 18px
  padding 8px 0

  &-item
    display grid
    grid-template-columns 24px 1fr
    align-items center
    column-gap 8px
    row-gap 4px

    & > *:not(:first-child)
      border-bottom 1px solid var(--border-secondary)
      padding-left 8px

    & > *:first-child
      grid-column 1
      grid-row 1 / 3
      display flex
      align-items center
      justify-content center
      width 24px
      height 24px
      border-radius 50%
      background-color rgba(18, 165, 62, .1)
      color #12a53e

.add-legal-account__presubmit
  display flex
  flex-direction column
  gap 8px
  padding 8px 0

.add-legal-account__actions
  display flex
  flex-direction column
  gap 16px

  &:deep(.mm-checkbox__label)
    padding-top 0;

  &-checkbox
    display flex
    align-items center
    gap 4px
</style>